import { Flex, Text } from "@/atoms"
import * as AdrDomain from "@/domain/adr"
import { DEFAULT_FORMAT, getDateInUserLocale } from "@/helpers/date.helpers"
import { Trans, useTranslation } from "@/hooks/useTranslation"
import {
  getEnforcementValues,
  getValues,
  translationsWithVM
} from "@/pages/TriagePage/components/pages/AdrPage/helpers"
import { Description } from "./Description"
import { PolicyViolationDescription } from "./PolicyViolationDescription"

import styles from "./OverviewStepsAdr.module.scss"

import type { CVEIncident } from "@/domain/adr"
import type { OverviewStepsAdrProps, StepStatus } from "."

const STEP_STATUSES: StepStatus[] = [
  "wait",
  "process",
  "finish",
  "error",
  "blocked"
]

const toStepStatus = (value: string, fallback: StepStatus): StepStatus =>
  STEP_STATUSES.find((status) => status === value) ?? fallback

export const Steps = (props: OverviewStepsAdrProps) => {
  const { incident } = props
  const { type, what, where, detectedAtUserLocale } = incident
  const { t: tAll } = useTranslation()
  const { t } = useTranslation("translation", {
    keyPrefix: `triage.adr.incidents.${type}.overview.steps`
  })

  const values = getValues({
    detectedAtUserLocale,
    ...what,
    ...where,
    ...getEnforcementValues(incident)
  })
  const attr = { t, values }

  const description1 = translationsWithVM("1.description", incident, attr)
  const status1 = toStepStatus(t("1.status", "finish"), "finish")
  const description2 = t("2.description", values)
  const status2 = toStepStatus(t("2.status", "finish"), "finish")
  const description3 = t("3.description", values)
  const description3Additional1 = t("3.description1", values)
  const status3 = toStepStatus(t("3.status", "error"), "error")
  const policyViolation =
    t("3.type", "") === "policyViolation" ? incident.enforcement : undefined
  const description4 = t("4.description", values)
  const status4 = toStepStatus(t("4.status", "finish"), "finish")
  const description5 = t("5.description", values)
  const status5 = toStepStatus(t("5.status", "finish"), "finish")
  const description6 = t("6.description", values)
  const status6 = toStepStatus(t("6.status", "finish"), "finish")

  const isMultipleCVE = AdrDomain.isMultipleCVE(incident)
  const isStatus3Error = status3 === "error"
  const isCVE = AdrDomain.isCVE(incident)
  const cveList =
    isMultipleCVE &&
    (what as CVEIncident["what"]).vulnerabilitiesMeta?.CVEs?.list
  const cveMaxLength = 2
  const cveRestLength = cveList && cveList.length - cveMaxLength
  const isStepFourVisible = t("4.title", values) !== ""
  const isStepFiveVisible = t("5.title", values) !== ""
  const isStepSixVisible = t("6.title", values) !== ""

  const title1 = translationsWithVM("1.title", incident, attr)
  const title2 = translationsWithVM("2.title", incident, attr)

  switch (type) {
    default:
      return [
        {
          title: (
            <Text>
              <Trans>{title1}</Trans>
            </Text>
          ),
          description: (
            <>
              {status1 === "wait" && <Description>{description1}</Description>}
              {status1 === "finish" && <Trans>{description1}</Trans>}
              {status1 === "blocked" && (
                <Description tag={description1} tagColor="success" />
              )}
            </>
          ),
          status: status1
        },
        {
          title: (
            <Text>
              <Trans>{title2}</Trans>
            </Text>
          ),
          description: (
            <>
              {status2 === "wait" && <Description>{description2}</Description>}
              {status2 === "error" && <Description tag={description2} />}
              {status2 === "blocked" && (
                <Description tag={description2} tagColor="success" />
              )}
              {status2 === "finish" && <Trans>{description2}</Trans>}
            </>
          ),
          status: status2
        },
        {
          title: (
            <Text>
              <Trans>{t("3.title", values)}</Trans>
            </Text>
          ),
          description: (
            <>
              {status3 === "finish" && <Trans>{description3}</Trans>}
              {isStatus3Error && !isMultipleCVE && policyViolation && (
                <PolicyViolationDescription
                  enforcement={policyViolation}
                  text={description3}
                />
              )}
              {isStatus3Error && !isMultipleCVE && !policyViolation && (
                <Description tag={description3} />
              )}
              {status3 === "blocked" && (
                <Description tag={description3} tagColor="success" />
              )}
              {isCVE && (
                <Flex vertical gap={4}>
                  {cveList && (
                    <Flex gap={4} className={styles.cveList}>
                      <Description
                        tag={cveList.slice(0, cveMaxLength).join(", ")}
                      />
                      {!!cveRestLength && (
                        <span className={styles.tagListCount}>
                          <Description
                            tag={`+${tAll("general.cve", { count: cveRestLength })}`}
                          />
                        </span>
                      )}
                    </Flex>
                  )}
                  {type === "POTENTIAL_EXPLOIT_CVE" && (
                    <Text type="secondary" className="sm-text-ks">
                      {`${getDateInUserLocale(incident.detectedAt, DEFAULT_FORMAT)} (${(what as CVEIncident["what"]).vulnerabilitiesMeta?.cve?.daysSinceBaseline} days after baseline was established)`}
                    </Text>
                  )}
                </Flex>
              )}
              <Description>{description3Additional1}</Description>
            </>
          ),
          status: status3
        },
        {
          title: (
            <Text>
              <Trans>{t("4.title", values)}</Trans>
            </Text>
          ),
          description: (
            <>
              {status4 === "wait" && <Description>{description4}</Description>}
              {status4 === "error" && <Description tag={description4} />}
              {status4 === "blocked" && (
                <Description tag={description4} tagColor="success" />
              )}
              {status4 === "finish" && <Trans>{description4}</Trans>}
            </>
          ),
          status: status4
        },
        {
          title: (
            <Text>
              <Trans>{t("5.title", values)}</Trans>
            </Text>
          ),
          description: (
            <>
              {status5 === "wait" && <Description>{description5}</Description>}
              {status5 === "error" && <Description tag={description5} />}
              {status5 === "blocked" && (
                <Description tag={description5} tagColor="success" />
              )}
              {status5 === "finish" && <Trans>{description5}</Trans>}
            </>
          ),
          status: status5
        },
        {
          title: (
            <Text>
              <Trans>{t("6.title", values)}</Trans>
            </Text>
          ),
          description: (
            <>
              {status6 === "wait" && <Description>{description6}</Description>}
              {status6 === "error" && <Description tag={description6} />}
              {status6 === "blocked" && (
                <Description tag={description6} tagColor="success" />
              )}
              {status6 === "finish" && <Trans>{description6}</Trans>}
            </>
          ),
          status: status6
        }
      ].filter((_, index) => {
        if (index === 3) return isStepFourVisible
        if (index === 4) return isStepFiveVisible
        if (index === 5) return isStepSixVisible

        return true
      })
  }
}
